import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { getLocalStorage, getUserRole } from "../../helpers/utils";
import { ShellProvider, useShell } from "./ShellContext";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import styles from "./AppShell.module.scss";

function ShellFrame({ role, user, children }) {
  const location = useLocation();
  const { collapsed, mobileNavOpen, closeMobileNav } = useShell();
  const resolvedRole = role || getUserRole();
  const resolvedUser = user || getLocalStorage("USER_DETAILS") || {};

  // Enable the antd coherence bridge while the shell is mounted.
  useEffect(() => {
    document.body.classList.add("cb-bridge");
    return () => document.body.classList.remove("cb-bridge");
  }, []);

  // Close mobile nav on route change.
  useEffect(() => {
    closeMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape closes mobile nav.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileNav();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileNavOpen, closeMobileNav]);

  // Lock body scroll while mobile drawer is open.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  return (
    <div
      className={styles.shell}
      data-collapsed={collapsed ? "true" : "false"}
      data-mobile-nav={mobileNavOpen ? "true" : "false"}
    >
      {mobileNavOpen ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation"
          onClick={closeMobileNav}
        />
      ) : null}
      <Sidebar role={resolvedRole} />
      <div className={styles.main}>
        <TopBar user={resolvedUser} />
        <div className={styles.content}>
          <div className={styles.contentInner}>{children}</div>
        </div>
      </div>
    </div>
  );
}

ShellFrame.propTypes = {
  role: PropTypes.string,
  user: PropTypes.object,
  children: PropTypes.node,
};

/**
 * Shared application shell — collapsible deep-slate Sidebar + TopBar wrapping
 * a scrollable content region. Every authenticated route renders inside it.
 */
export default function AppShell({ role, user, children }) {
  return (
    <ShellProvider>
      <ShellFrame role={role} user={user}>
        {children}
      </ShellFrame>
    </ShellProvider>
  );
}

AppShell.propTypes = {
  role: PropTypes.string,
  user: PropTypes.object,
  children: PropTypes.node,
};
